/**
 * API Client Utilities
 * Provides authentication headers for backend API calls
 */

import { refreshSession } from '../auth/cognitoAuth';
import { emitSessionExpired } from '../auth/sessionEvents';

/**
 * Get authentication headers for API requests
 * Automatically retrieves and includes the current user's JWT token
 * Uses ID token which contains custom attributes like organizationId
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  try {
    const tokens = await Promise.race([
      refreshSession(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 2500)),
    ]);

    if (tokens && tokens.idToken) {
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokens.idToken}`
      };
    }
  } catch (error) {
    console.error('[apiClient] Failed to get auth token:', error);
  }

  // No usable session yet — this alone isn't proof the session is dead (it
  // can happen before Cognito has hydrated, or a race with other callers),
  // so don't emit session-expired here. A request actually sent to the
  // backend with a bad/missing token will come back 401, which is the
  // signal authenticatedFetch() below treats as confirmed expiry.
  console.error('[apiClient] No valid session; sending request unauthenticated');
  return {
    'Content-Type': 'application/json'
  };
}

/** Requests hang forever with no feedback if the backend is unresponsive (e.g. mid-deploy
 * container restart) — every call site relies on this default so a stuck backend surfaces
 * as a catchable error instead of an infinite spinner.
 * Local Athena/Iceberg Control Centre queries often exceed 30s on cold cache. */
const DEFAULT_TIMEOUT_MS = import.meta.env.DEV ? 300_000 : 30_000;

/**
 * Make an authenticated API request
 * @param url - The API endpoint URL
 * @param options - Fetch options (method, body, etc.)
 * @param timeoutMs - Abort the request after this many ms (default 30s). Pass 0 to disable.
 *
 * Defaults to `cache: 'no-store'`. Control Centre auto-refresh hits the same
 * URLs repeatedly; without this the browser sends conditional requests, Express
 * etag returns 304 with an empty body, and callers that check `res.ok` /
 * `res.json()` treat a successful re-read as failure and wipe the UI.
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<Response> {
  const authHeaders = await getAuthHeaders();

  const controller = timeoutMs > 0 && !options.signal ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(new DOMException(`Request timed out after ${timeoutMs}ms: ${url}`, 'TimeoutError')), timeoutMs)
    : null;

  try {
    const response = await fetch(url, {
      ...options,
      cache: options.cache ?? 'no-store',
      headers: {
        ...authHeaders,
        ...options.headers
      },
      signal: options.signal ?? controller?.signal
    });

    if (response.status === 401) {
      emitSessionExpired();
    }

    return response;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export default {
  getAuthHeaders,
  authenticatedFetch
};
